import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { getSignerContractByAddress, Deposit, DepositState, formatAmount, getStateString, getTokenSymbol, formatTimestamp } from '../contracts/contract';
import { NetworkConfig } from '../config/networks';
import { trustlineService } from '../services/trustline';
import './DepositList.css';

interface DepositListProps {
  deposits: Deposit[];
  signer: ethers.Signer;
  network: NetworkConfig;
  userAddress: string;
  provider: ethers.Provider;
  contractAddress: string;
  arbiterAddress: string;
  onAction: (depositId: number, expectedState: number) => void;
  onInitiateTransfer?: () => void;
}

const DepositList: React.FC<DepositListProps> = ({ 
  deposits, 
  signer, 
  network, 
  userAddress, 
  provider, 
  contractAddress,
  arbiterAddress,
  onAction,
  onInitiateTransfer
}) => {
  const [loading, setLoading] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [tokenSymbols, setTokenSymbols] = useState<Map<string, string>>(new Map());

  // Fetch token symbols when deposits change
  useEffect(() => {
    const fetchTokenSymbols = async () => {
      if (!provider || deposits.length === 0) return;

      const newSymbols = new Map<string, string>();
      const uniqueTokens = Array.from(new Set(deposits.map(deposit => deposit.token)));
      
      for (const tokenAddress of uniqueTokens) {
        if (tokenAddress !== ethers.ZeroAddress) {
          try {
            const symbol = await getTokenSymbol(provider, tokenAddress);
            newSymbols.set(tokenAddress, symbol);
          } catch (error) {
            console.warn(`Failed to fetch symbol for ${tokenAddress}:`, error);
            newSymbols.set(tokenAddress, 'Token');
          }
        } else {
          newSymbols.set(tokenAddress, 'ETH');
        }
      }
      
      setTokenSymbols(newSymbols);
    };

    fetchTokenSymbols();
  }, [provider, deposits]);

  const handleRelease = async (depositId: number) => {
    setLoading(depositId);
    setError('');

    try {
      const contract = getSignerContractByAddress(signer, contractAddress);
      const tx = await contract.release(depositId);
      await tx.wait();
      onAction(depositId, DepositState.Released);
    } catch (err) {
      setError('Release failed: ' + (err as Error).message);
    } finally {
      setLoading(null);
    }
  };

  const handleWithdraw = async (depositId: number) => {
    setLoading(depositId);
    setError('');

    try {
      // Trustline validation for withdraw (beneficiary)
      const isApproved = await trustlineService.validateWithdraw(
        network.chainId,
        userAddress,
        contractAddress,
        depositId
      );

      if (!isApproved) {
        throw new Error('Withdraw transaction not approved by Trustline compliance check');
      }

      const contract = getSignerContractByAddress(signer, contractAddress);
      const tx = await contract.withdraw(depositId);
      await tx.wait();
      onAction(depositId, DepositState.Released);
    } catch (err) {
      setError('Withdraw failed: ' + (err as Error).message);
    } finally {
      setLoading(null);
    }
  };

  const handleRefund = async (depositId: number) => {
    setLoading(depositId);
    setError('');

    try {
      // Trustline validation for refund is disabled
      // const isApproved = await trustlineService.validateRefund(
      //   network.chainId,
      //   userAddress,
      //   network.contractAddress,
      //   depositId
      // );

      // if (!isApproved) {
      //   throw new Error('Refund transaction not approved by Trustline compliance check');
      // }

      const contract = getSignerContractByAddress(signer, contractAddress);
      const tx = await contract.refund(depositId);
      await tx.wait();
      onAction(depositId, DepositState.Refunded);
    } catch (err) {
      setError('Refund failed: ' + (err as Error).message);
    } finally {
      setLoading(null);
    }
  };

  const canRelease = (deposit: Deposit): boolean => {
    return deposit.state === DepositState.Deposited && 
           (arbiterAddress.toLowerCase() === userAddress.toLowerCase());
  };

  const canWithdraw = (deposit: Deposit): boolean => {
    return deposit.state === DepositState.Deposited && 
           (deposit.beneficiary.toLowerCase() === userAddress.toLowerCase());
  };

  const canRefund = (deposit: Deposit): boolean => {
    return deposit.state === DepositState.Deposited && 
           ((arbiterAddress.toLowerCase() === userAddress.toLowerCase()) ||
           (deposit.depositor.toLowerCase() === userAddress.toLowerCase()));
  };

  const formatAddress = (address: string): string => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here if desired
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const getTokenSymbolDisplay = (tokenAddress: string): string => {
    return tokenSymbols.get(tokenAddress) || (tokenAddress === ethers.ZeroAddress ? 'ETH' : 'Token');
  };

  if (deposits.length === 0) {
    return (
      <div className="deposit-list">
        <h3>Deposits</h3>
        <p className="no-deposits">No deposits found</p>
      </div>
    );
  }

  // Sort deposits by ID in descending order (highest first)
  const sortedDeposits = [...deposits].sort((a, b) => {
    return b.id - a.id;
  });

  return (
    <div className="deposit-list">
      <div className="deposit-list-header">
        <h3>Deposits [{deposits.length}]</h3>
        {onInitiateTransfer && (
          <button onClick={onInitiateTransfer} className="initiate-transfer-btn">
            Make a Deposit
          </button>
        )}
      </div>
      
      {error && <div className="error">{error}</div>}
      
      <div className="deposits-grid">
        {sortedDeposits.map((deposit) => {
          return (
          <div key={deposit.id} className="deposit-card">
            <div className="deposit-header">
              <span className="deposit-id">#{deposit.id}</span>
              <span className={`deposit-state state-${deposit.state}`}>
                {getStateString(deposit.state)}
              </span>
            </div>
            
            <div className="deposit-details">
              <div className="detail-row">
                <span className="label">Amount:</span>
                <span className="value">
                  {formatAmount(deposit.amount)} {getTokenSymbolDisplay(deposit.token)}
                </span>
              </div>
              
              <div className="detail-row">
                <span className="label">Depositor:</span>
                <span className={`value ${deposit.depositor.toLowerCase() === userAddress.toLowerCase() ? 'highlight-address' : 'regular-address'}`}>
                  {formatAddress(deposit.depositor)}
                  <button 
                    className="copy-btn" 
                    onClick={() => copyToClipboard(deposit.depositor)}
                    title="Copy full address"
                  >
                    📋
                  </button>
                </span>
              </div>
              
              <div className="detail-row">
                <span className="label">Beneficiary:</span>
                <span className={`value ${deposit.beneficiary.toLowerCase() === userAddress.toLowerCase() ? 'highlight-address' : 'regular-address'}`}>
                  {formatAddress(deposit.beneficiary)}
                  <button 
                    className="copy-btn" 
                    onClick={() => copyToClipboard(deposit.beneficiary)}
                    title="Copy full address"
                  >
                    📋
                  </button>
                </span>
              </div>
              
              {deposit.token !== ethers.ZeroAddress && (
                <div className="detail-row">
                  <span className="label">Token:</span>
                  <span className="value">{formatAddress(deposit.token)}</span>
                </div>
              )}
              
              <div className="detail-row">
                <span className="label">Deposited:</span>
                <span className="value">{formatTimestamp(deposit.depositTimestamp)}</span>
              </div>
              
              <div className="detail-row">
                <span className="label">Completed:</span>
                <span className="value">{formatTimestamp(deposit.completionTimestamp)}</span>
              </div>
            </div>
            
            {deposit.state === DepositState.Deposited && (
              <div className="deposit-actions">
                {canRelease(deposit) && (
                  <button
                    onClick={() => handleRelease(deposit.id)}
                    disabled={loading === deposit.id}
                    className="action-btn release-btn"
                  >
                    {loading === deposit.id ? 'Processing...' : 'Release'}
                  </button>
                )}
                
                {canWithdraw(deposit) && (
                  <button
                    onClick={() => handleWithdraw(deposit.id)}
                    disabled={loading === deposit.id}
                    className="action-btn withdraw-btn"
                  >
                    {loading === deposit.id ? 'Processing...' : 'Withdraw'}
                  </button>
                )}
                
                {canRefund(deposit) && (
                  <button
                    onClick={() => handleRefund(deposit.id)}
                    disabled={loading === deposit.id}
                    className="action-btn refund-btn"
                  >
                    {loading === deposit.id ? 'Processing...' : 'Refund'}
                  </button>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default DepositList;
